/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	computed,
	inject,
	signal,
} from "@angular/core";
import { NavigationEnd, RouterModule, RouterOutlet } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import { CbRoutesUtils } from "./@classes/routes/cb-routes-utils";
import { AppView } from "./@classes/views/common/app.view";
import {
	type CbFeature,
	FeaturesService,
} from "./@services/shared/features.service";
import { GlobalTemplateModule } from "./directives/global-template/global-template.module";
import { LayoutModule } from "./widgets/base/layout/layout.module";
import {
	type CbSidebarItem,
	SidebarComponent,
} from "./widgets/base/sidebar/sidebar.component";

import { environment } from "src/environments/environment";
import { AuthTenantService } from "./@services/shared/auth-tenant.service";
import { ChoiceWidgetComponent } from "./widgets/base/choice/choice-widget/choice-widget.component";
import { EmptyStateComponent } from "./widgets/base/empty-state/empty-state.component";
import { LoaderModule } from "./widgets/base/loader/loader.module";
import {
	type CbTopNavbarItem,
	TopNavbarComponent,
} from "./widgets/base/top-navbar/top-navbar.component";

@Component({
	selector: "app-root",
	imports: [
		RouterOutlet,
		LayoutModule,
		SidebarComponent,
		TranslateModule,
		RouterModule,
		GlobalTemplateModule,
		EmptyStateComponent,
		LoaderModule,
		TopNavbarComponent,
		ChoiceWidgetComponent,
	],
	templateUrl: "./app.component.html",
	styleUrl: "./app.component.scss",
	providers: [],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends AppView {
	private readonly tenantsService: AuthTenantService =
		inject(AuthTenantService);
	private readonly featuresService: FeaturesService = inject(FeaturesService);

	public screenWidth = signal(0);
	public screenHeight = signal(0);

	private destroy$: Subject<void> = new Subject<void>();

	public hasStripeSecretKey = false;

	public isBackoffice = false;

	public isDev = environment.dev;

	public currentOffice = signal<
		"warehouse" | "administration" | "planning" | undefined
	>(undefined);

	public readonly sidebarNavItems = computed(() => {
		switch (this.currentOffice()) {
			case "warehouse":
				return this.sidebarNavWarehouseItems;
			case "administration":
				return this.sidebarNavAdministrationItems;
			case "planning":
				return this.sidebarNavPlanningItems;
			default:
				return [];
		}
	});

	public readonly sidebarFooterItems = computed(() => {
		switch (this.currentOffice()) {
			case "warehouse":
				return this.sidebarFooterWarehouseItems;
			case "administration":
				return this.sidebarFooterAdministrationItems;
			case "planning":
				return this.sidebarFooterPlanningItems;
			default:
				return [];
		}
	});

	public readonly background = computed(() => {
		switch (this.currentOffice()) {
			case "warehouse":
				return "linear-gradient(90deg, #001455 0%, #5925DC 29.67%, #001E80 51.91%, #001455 100%)";
			case "planning":
				return "linear-gradient(90deg, #095C37 0%, #099250 29.67%, #074D31 51.91%, #095C37 100%)";
			case "administration":
				return "linear-gradient(90deg, #57130A 0%, #BC1B06 29.67%, #9E2008 51.91%, #57130A 100%)";
		}
		return "linear-gradient(90deg, #001455 0%, #7839EE 29.67%, #001E80 51.91%, #001455 100%)";
	});

	public readonly sidebarNavWarehouseItems: CbTopNavbarItem[] = [
		{
			type: "route",
			//label: this.features["w_home"].title,
			routerLink: this.features["w_home"].path,
			icon: this.features["w_home"].icon,
		},
		{
			type: "route",
			label: this.features["products"].title,
			routerLink: this.features["products"].path,
			icon: this.features["products"].icon,
			color: "#FF4405",
		},
		{
			type: "route",
			label: this.features["warehouseManagement"].title,
			routerLink: this.features["warehouseManagement"].path,
			icon: this.features["warehouseManagement"].icon,
			color: "#0BA5EC",
		},
		{
			type: "route",
			label: this.features["incomingOrders"].title,
			routerLink: this.features["incomingOrders"].path,
			icon: this.features["incomingOrders"].icon,
			color: "#D444F1",
		},
		{
			type: "route",
			label: this.features["outgoingOrders"].title,
			routerLink: this.features["outgoingOrders"].path,
			icon: this.features["outgoingOrders"].icon,
			color: "#15B79E",
		},
		{
			type: "route",
			label: this.features["w_companies"].title,
			routerLink: this.features["w_companies"].path,
			icon: this.features["w_companies"].icon,
			color: "#EAAA08",
		},
		{
			type: "route",
			label: this.features["w_emails"].title,
			routerLink: this.features["w_emails"].path,
			icon: this.features["w_emails"].icon,
			show: this.features["w_emails"].canAccess,
			color: "#6938EF",
		},
		{
			type: "route",
			label: this.features["warehouse"].title,
			routerLink: this.features["warehouse"].path,
			icon: this.features["warehouse"].icon,
			show: this.features["warehouse"].canAccess,
			color: "#335BDD",
		},
		{
			type: "group",
			icon: "cb-base-outline-more",
			items: [
				{
					type: "route",
					label: this.features["works"].title,
					routerLink: this.features["works"].path,
					icon: this.features["works"].icon,
					show: this.features["works"].canAccess,
					color: "#0BA5EC",
				},
				{
					type: "route",
					label: this.features["alerts"].title,
					routerLink: this.features["alerts"].path,
					icon: this.features["alerts"].icon,
					show: this.features["alerts"].canAccess,
					color: "#EAAA08",
				},
				{
					type: "route",
					label: this.features["palmtop_operations"].title,
					routerLink: this.features["palmtop_operations"].path,
					icon: this.features["palmtop_operations"].icon,
					show: this.features["palmtop_operations"].canAccess,
					color: "#10B981",
				},
				{
					type: "route",
					label: this.features["inventory"].title,
					routerLink: this.features["inventory"].path,
					icon: this.features["inventory"].icon,
					show: this.features["inventory"].canAccess,
					color: "#0BA5EC",
				},
			],
		},
	];

	public readonly sidebarNavAdministrationItems: CbTopNavbarItem[] = [
		{
			type: "route",
			//label: this.features["home"].title,
			routerLink: this.features["a_home"].path,
			icon: this.features["a_home"].icon,
		},
		{
			type: "route",
			label: this.features["invoices"].title,
			routerLink: this.features["invoices"].path,
			icon: this.features["invoices"].icon,
			color: "#EAAA08",
		},
		{
			type: "route",
			label: this.features["vehicles"].title,
			routerLink: this.features["vehicles"].path,
			icon: this.features["vehicles"].icon,
			color: "#EAAA08",
		},
		{
			type: "route",
			label: this.features["a_companies"].title,
			routerLink: this.features["a_companies"].path,
			icon: this.features["a_companies"].icon,
			color: "#EAAA08",
		},
		{
			type: "route",
			label: this.features["calendar"].title,
			routerLink: this.features["calendar"].path,
			icon: this.features["calendar"].icon,
			color: "#0BA5EC",
		},
		{
			type: "route",
			label: this.features["staff_members"].title,
			routerLink: this.features["staff_members"].path,
			icon: this.features["staff_members"].icon,
			color: "#0BA5EC",
		},
		{
			type: "route",
			label: this.features["a_emails"].title,
			routerLink: this.features["a_emails"].path,
			icon: this.features["a_emails"].icon,
			color: "#0BA5EC",
		},
		{
			type: "route",
			label: this.features["pre_billing"].title,
			routerLink: this.features["pre_billing"].path,
			icon: this.features["pre_billing"].icon,
			color: "#0BA5EC",
		},
	];

	public readonly sidebarNavPlanningItems: CbTopNavbarItem[] = [
		{
			type: "route",
			//label: this.features["home"].title,
			routerLink: this.features["p_home"].path,
			icon: this.features["p_home"].icon,
		},
		{
			type: "route",
			label: this.features["pickups"].title,
			routerLink: this.features["pickups"].path,
			icon: this.features["pickups"].icon,
			color: "#0BA5EC",
		},
		{
			type: "route",
			label: this.features["p_companies"].title,
			routerLink: this.features["p_companies"].path,
			icon: this.features["p_companies"].icon,
			color: "#0BA5EC",
		},
	];

	// public readonly sidebarFooterItems: CbTopNavbarItem[] = [
	// 	{
	// 		type: "route",
	// 		//label: "SECTIONS.ROUTES.BACKOFFICE.TITLE",
	// 		routerLink: CbRoutesUtils.paths.backoffice.path,
	// 		icon: "cb-base-bold-admin",
	// 		showSignal: this.authStateService.isSuperadmin,
	// 	},
	// 	{
	// 		type: "route",
	// 		//label: this.features["settings"].title,
	// 		routerLink: this.features["settings"].path,
	// 		icon: this.features["settings"].icon,
	// 	},
	// ];

	public readonly sidebarFooterWarehouseItems: CbTopNavbarItem[] = [
		{
			type: "route",
			//label: "SECTIONS.ROUTES.BACKOFFICE.TITLE",
			routerLink: CbRoutesUtils.paths.backoffice.path,
			icon: "cb-base-bold-admin",
			showSignal: this.authStateService.isSuperadmin,
		},
		{
			type: "route",
			//label: this.features["settings"].title,
			routerLink: this.features["w_settings"].path,
			icon: this.features["w_settings"].icon,
		},
	];

	public readonly sidebarFooterAdministrationItems: CbTopNavbarItem[] = [
		{
			type: "route",
			//label: "SECTIONS.ROUTES.BACKOFFICE.TITLE",
			routerLink: CbRoutesUtils.paths.backoffice.path,
			icon: "cb-base-bold-admin",
			showSignal: this.authStateService.isSuperadmin,
		},
		{
			type: "route",
			//label: this.features["settings"].title,
			routerLink: this.features["a_settings"].path,
			icon: this.features["a_settings"].icon,
		},
	];

	public readonly sidebarFooterPlanningItems: CbTopNavbarItem[] = [
		{
			type: "route",
			//label: "SECTIONS.ROUTES.BACKOFFICE.TITLE",
			routerLink: CbRoutesUtils.paths.backoffice.path,
			icon: "cb-base-bold-admin",
			showSignal: this.authStateService.isSuperadmin,
		},
		{
			type: "route",
			//label: this.features["settings"].title,
			routerLink: this.features["p_settings"].path,
			icon: this.features["p_settings"].icon,
		},
	];

	public readonly backofficeNavItems: CbSidebarItem[] = [
		{
			type: "route",
			label: "SECTIONS.ROUTES.BACKOFFICE.TENANTS",
			routerLink: CbRoutesUtils.paths.backoffice.tenants.path,
			icon: "cb-base-outline-tenant",
			//show: () => this.authService.isSuperadmin,
		},
		{
			type: "route",
			label: "SECTIONS.ROUTES.BACKOFFICE.USERS",
			routerLink: CbRoutesUtils.paths.backoffice.users.path,
			icon: "cb-base-outline-users",
			//show: () => this.authService.isSuperadmin
		},
		{
			type: "route",
			label: "SECTIONS.ROUTES.BACKOFFICE.AIPANEL",
			routerLink: CbRoutesUtils.paths.backoffice.ai.path,
			icon: "cb-base-outline-ai",
			//show: () => this.authService.isSuperadmin
		},
	];

	public readonly backofficeFooterItems: CbSidebarItem[] = [
		{
			type: "route",
			label: "SECTIONS.ROUTES.BACKOFFICE.SELECT_TENANT",
			routerLink: CbRoutesUtils.paths.tenant.path,
			icon: "cb-base-outline-refresh",
			//show: () => this.authService.isSuperadmin,
		},
	];

	/* @HostListener("window:resize", ["$event"])
	onWindowResize() {
		// this.screenWidth.set(window.innerWidth);
		// this.screenHeight.set(window.innerHeight);
		// if (window.innerWidth < 768) {
		// 	this.router.navigate([CbRoutesUtils.paths.w.palmtop.home.path]);
		// }
	}

	@HostListener("document:mousemove", ["$event"])
	onMouseMove(event: MouseEvent) {
		this.realtimeCursorsService.updateCursor$.next({
			x: (event.clientX / this.screenWidth()) * 100,
			y: (event.clientY / this.screenHeight()) * 100,
		});
	} */

	@HostListener("document:visibilitychange", [])
	onVisibilityChange() {
		if (document.visibilityState === "visible") {
			// La pagina è diventata visibile
			this.handlePageVisible();
		} else {
			// La pagina è nascosta
			this.handlePageHidden();
		}
	}

	handlePageVisible() {
		// Logica da eseguire quando la pagina diventa visibile
		// Ad esempio, aggiornare i dati o verificare lo stato della sessione
		this.authService.onPageVisibilityChange();
	}

	handlePageHidden() {
		// Logica da eseguire quando la pagina diventa nascosta
		// Ad esempio, sospendere aggiornamenti o salvare lo stato
	}

	public readonly isLoadingPrivileges = computed(() => {
		return this.featuresService.isLoading();
	});

	public readonly userId = computed(() => {
		return this.authStateService.currentUserId();
	});

	public readonly tenantName = computed(() => {
		return this.authStateService.currentTenantInfo()?.name;
	});

	constructor() {
		super();

		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
			if (event instanceof NavigationEnd) {
				const children = this.activatedRoute.children;
				if (children?.length > 0) {
					const data = children[0].snapshot.data;
					this.isBackoffice = data?.["backoffice"] ?? false;
					this.currentOffice.set(
						data?.["office"] as
							| "warehouse"
							| "administration"
							| "planning"
							| undefined,
					);
					return;
				}

				this.isBackoffice = false;
			}
		});
	}

	ngOnInit() {
		console.log("isDev", this.isDev);
		if (this.isDev) {
			this.confirmationService
				.show({
					title: "COMMON.DEV_MODE",
					message: "COMMON.DEV_MODE_MESSAGE",
					dangerous: true,
					confirmText: "COMMON.CONFIRM",
				})
				.subscribe((result) => {
					if (!result) {
						window.location.href = "https://corehub.colibryx.com";
					}
				});
		}
		this.screenWidth.set(window.innerWidth);
		this.screenHeight.set(window.innerHeight);

		// if (window.innerWidth < 768) {
		// 	this.router.navigate([CbRoutesUtils.paths.w.palmtop.home.path]);
		// }

		// Override structuredClone for older browsers //
		try {
			structuredClone({});
		} catch (e) {
			window.structuredClone = (obj: unknown) => {
				return JSON.parse(JSON.stringify(obj));
			};
		}
		// --- //
	}

	public get hasResponsive(): boolean {
		return true;
		//return this.featuresService.canSelect(["responsive"]);
	}

	private get features(): Record<string, CbFeature> {
		return this.featuresService.features;
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
